import React, { useState, useEffect, useCallback, createContext } from "react";
import { useSnackbar } from "notistack";

export const RenameJobsContext = createContext();

export default function RenameJobsProvider({ children }) {
  const { enqueueSnackbar } = useSnackbar();
  const [activeJobs, setActiveJobs] = useState([]);

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("renameJobs");
    if (saved) {
      setActiveJobs(JSON.parse(saved));
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem("renameJobs", JSON.stringify(activeJobs));
  }, [activeJobs]);

  const fetchJobStatus = useCallback(
    async (jobId) => {
      try {
        const response = await fetch(
          `https://staging-api.justact.co.in/rename-status/${jobId}`,
        );
        if (!response.ok) return;

        const data = await response.json();

        setActiveJobs((prev) =>
          prev.map((job) =>
            job.job_id === jobId
              ? {
                  ...job,
                  status: data.status,
                  url: data.url || job.url,
                  error: data.error || null,
                }
              : job,
          ),
        );

        if (data.status === "completed" && data.url) {
          enqueueSnackbar("Rename completed successfully!", {
            variant: "success",
          });
        } else if (data.status === "failed") {
          enqueueSnackbar("Job failed.", { variant: "error" });
        }
      } catch (error) {
        console.error("Job status fetch error:", error);
      }
    },
    [enqueueSnackbar],
  );

  // Global Polling - Independent of page
  useEffect(() => {
    if (activeJobs.length === 0) return;

    const interval = setInterval(() => {
      activeJobs.forEach((job) => {
        if (job.status === "processing" || job.status === null || !job.status) {
          fetchJobStatus(job.job_id);
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [activeJobs, fetchJobStatus]);

  const addJob = (newJob) => {
    setActiveJobs((prev) => [newJob, ...prev]);
  };

  const removeJob = (jobId) => {
    setActiveJobs((prev) => prev.filter((job) => job.job_id !== jobId));
  };

  const clearCompleted = () => {
    setActiveJobs((prev) =>
      prev.filter(
        (job) => job.status !== "completed" && job.status !== "failed",
      ),
    );
  };

  const value = {
    activeJobs,
    addJob,
    removeJob,
    clearCompleted,
    fetchJobStatus,
  };

  return (
    <RenameJobsContext.Provider value={value}>
      {children}
    </RenameJobsContext.Provider>
  );
}
